import React from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, Text, StyleProp, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import SegmentedControl from '../common/SegmentedControl';
import { useTheme } from '../../theme/ThemeContext';

export type SegmentOption<T extends string> = {
  label: string;
  value: T;
};

export type SubTabOption<T extends string> = SegmentOption<T>;

export type SubTabConfig<T extends string> = {
  options: SubTabOption<T>[];
  selectedValue: T;
  onChange: (value: T) => void;
};

export type SearchConfig = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
};

export type ScreenCommandHeaderProps<SegmentValue extends string = string, SubValue extends string = string> = {
  segments: SegmentOption<SegmentValue>[];
  selectedValue: SegmentValue;
  onValueChange: (value: SegmentValue) => void;
  subTabs?: SubTabConfig<SubValue>;
  searchConfig?: SearchConfig;
  actionComponent?: React.ReactNode;
  footerContent?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
};

function ScreenCommandHeader<SegmentValue extends string = string, SubValue extends string = string>({
  segments,
  selectedValue,
  onValueChange,
  subTabs,
  searchConfig,
  actionComponent,
  footerContent,
  containerStyle,
}: ScreenCommandHeaderProps<SegmentValue, SubValue>) {
  const { theme } = useTheme();
  
  // Only add bottom padding if there's content after tabs
  const hasContentAfterTabs = subTabs || searchConfig || actionComponent || footerContent;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.backgroundSecondary,
          shadowColor: theme.shadow,
          paddingBottom: hasContentAfterTabs ? 12 : 0,
        },
        containerStyle,
      ]}
    >
      <SegmentedControl
        segments={segments}
        selectedValue={selectedValue}
        onValueChange={onValueChange}
      />

      {subTabs && (
        <View style={[styles.subTabRow, { borderBottomColor: theme.border }]}>
          {subTabs.options.map((option) => {
            const isActive = option.value === subTabs.selectedValue;
            return (
              <TouchableOpacity
                key={option.value}
                style={styles.subTab}
                onPress={() => subTabs.onChange(option.value)}
              >
                <Text
                  style={[
                    styles.subTabText,
                    { color: isActive ? theme.primary : theme.textSecondary },
                  ]}
                >
                  {option.label}
                </Text>
                {isActive && (
                  <View
                    style={[
                      styles.activeIndicator,
                      { backgroundColor: theme.primary },
                    ]}
                  />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {(searchConfig || actionComponent) && (
        <View style={[styles.commandRow, !subTabs && styles.commandRowNoSubTabs]}>
          {searchConfig && (
            <View style={[styles.searchBar, { backgroundColor: theme.backgroundTertiary }]}>
              <Ionicons name="search" size={20} color={theme.textTertiary} />
              <TextInput
                style={[styles.searchInput, { color: theme.text }]}
                placeholder={searchConfig.placeholder}
                placeholderTextColor={theme.placeholder}
                value={searchConfig.value}
                onChangeText={searchConfig.onChange}
              />
              {searchConfig.value.length > 0 && (
                <TouchableOpacity
                  onPress={() => (searchConfig.onClear ? searchConfig.onClear() : searchConfig.onChange(''))}
                  style={styles.clearButton}
                >
                  <Ionicons name="close-circle" size={20} color={theme.textTertiary} />
                </TouchableOpacity>
              )}
            </View>
          )}
          {actionComponent && <View style={styles.actionSlot}>{actionComponent}</View>}
        </View>
      )}

      {footerContent && <View style={styles.footer}>{footerContent}</View>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  subTabRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginTop: 0,
    marginBottom: 12,
    borderBottomWidth: 1,
  },
  subTab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    position: 'relative',
  },
  activeIndicator: {
    position: 'absolute',
    bottom: -1,
    left: '10%',
    right: '10%',
    height: 3,
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
  },
  subTabText: {
    fontSize: 16,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
  commandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 0,
    gap: 12,
  },
  commandRowNoSubTabs: {
    paddingTop: 0,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    height: 44,
    borderRadius: 999,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },
  clearButton: {
    padding: 4,
  },
  actionSlot: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    marginTop: 12,
  },
});

export default ScreenCommandHeader;
